// Cross-platform notification preferences, Web Push enrollment, and desktop fallback.

const NOTIFICATION_DEFAULTS = {
  enabled:false,
  penSupply:true,
  vialPreparation:true,
  freezerTemperature:true,
  protocolStart:true,
  syncFailure:true,
  daysBefore:5,
  freezerMaximumC:-15,
}

function notificationPreferences() {
  return Object.assign({}, NOTIFICATION_DEFAULTS, S.settings.notifications || {})
}

function notificationApiUrl(path) {
  const base = String(S.settings.cloudUrl || '')
  return base ? base.replace(/\/api\/data(?:\?.*)?$/, path) : ''
}

function base64UrlToUint8Array(value) {
  const padding = '='.repeat((4 - value.length % 4) % 4)
  const raw = atob((value + padding).replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from(Array.from(raw).map(function(char){ return char.charCodeAt(0) }))
}

function notificationSnapshot() {
  const prefs = notificationPreferences()
  const loads = typeof homeVialWorkflow === 'function' ? homeVialWorkflow().map(function(load) {
    return {
      key:load.key,
      name:(load.peptide && load.peptide.name) || load.protocol.peptideName || load.protocol.name || 'Peptide',
      protocolName:load.protocol.name || load.protocol.peptideName || 'Protocol',
      protocolStart:load.protocol.startDate || '',
      stage:load.stage,
      runoutDate:load.runoutDate || '',
      daysUntilRunout:load.daysUntilRunout,
    }
  }) : []
  const sync = typeof getSyncRuntimeStatus === 'function' ? getSyncRuntimeStatus() : {}
  return {
    version:typeof APP_VERSION === 'undefined' ? '' : APP_VERSION,
    generatedAt:new Date().toISOString(),
    preferences:prefs,
    sensor:{ sku:S.settings.goveeDeviceSku || '', device:S.settings.goveeDeviceId || '', name:S.settings.goveeDeviceName || 'Freezer' },
    freezer:S._environmentSensorStatus && S._environmentSensorStatus.state === 'connected' ? S._environmentSensorStatus.reading : null,
    protocols:(S.protocols || []).filter(function(protocol){ return protocol.status === 'active' || protocol.status === 'upcoming' }).map(function(protocol){
      return { id:protocol.id, name:protocol.name || protocol.peptideName || 'Protocol', status:protocol.status, startDate:protocol.startDate || '', endDate:protocol.endDate || '' }
    }),
    loads:loads,
    syncError:sync.error ? { at:sync.error.at || new Date().toISOString(), message:sync.error.message || 'Cloud synchronization failed' } : null,
  }
}

async function notificationRequest(path, body) {
  const url = notificationApiUrl(path)
  if (!url || !S.settings.cloudToken) throw new Error('Cloud Sync must be connected first')
  const response = await fetch(url, {
    method:body ? 'POST' : 'GET',
    headers:{ 'Content-Type':'application/json', 'X-Token':S.settings.cloudToken },
    body:body ? JSON.stringify(body) : undefined,
  })
  const result = await response.json().catch(function(){ return {} })
  if (!response.ok || !result.ok) throw new Error(result.error || ('HTTP ' + response.status))
  return result
}

async function enableNotifications() {
  const prefs = notificationPreferences()
  if (isElectron) {
    prefs.enabled = true
    S.settings.notifications = prefs
    await saveSettings()
    await window.api.showNotification({ title:'PeptideVault notifications enabled', body:'This computer will show alerts while PeptideVault is running.', tab:'dashboard' })
    render(); return { ok:true, mode:'desktop' }
  }
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) throw new Error('Push notifications are not supported on this device')
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') throw new Error('Notification permission was not granted')
  const registration = await navigator.serviceWorker.ready
  const config = await notificationRequest('/api/notifications/config')
  let subscription = await registration.pushManager.getSubscription()
  if (!subscription) subscription = await registration.pushManager.subscribe({ userVisibleOnly:true, applicationServerKey:base64UrlToUint8Array(config.publicKey) })
  prefs.enabled = true
  S.settings.notifications = prefs
  await saveSettings()
  await notificationRequest('/api/notifications/subscribe', { subscription:subscription.toJSON(), deviceId:syncDeviceId(), preferences:prefs, snapshot:notificationSnapshot() })
  render(); return { ok:true, mode:'web-push' }
}

async function disableNotifications() {
  const prefs = notificationPreferences(); prefs.enabled = false
  S.settings.notifications = prefs; await saveSettings()
  if (!isElectron && 'serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (subscription) {
      await notificationRequest('/api/notifications/unsubscribe', { endpoint:subscription.endpoint }).catch(function(){})
      await subscription.unsubscribe()
    }
  }
  render()
}

async function saveNotificationPreferences() {
  const prefs = notificationPreferences()
  ;['penSupply','vialPreparation','freezerTemperature','protocolStart','syncFailure'].forEach(function(key){
    const input = document.getElementById('pv-notify-' + key)
    if (input) prefs[key] = !!input.checked
  })
  const days = document.getElementById('pv-notify-days')
  const freezer = document.getElementById('pv-notify-freezer')
  if (days) prefs.daysBefore = Math.max(1,Math.min(30,Number(days.value)||5))
  if (freezer) prefs.freezerMaximumC = Number(freezer.value)
  S.settings.notifications = prefs; await saveSettings(); await syncNotificationSnapshot(true); render(); showToast('Notification preferences saved')
}

let notificationSyncTimer = null
function syncNotificationSnapshotSoon() {
  if (!notificationPreferences().enabled) return
  clearTimeout(notificationSyncTimer)
  notificationSyncTimer = setTimeout(function(){ syncNotificationSnapshot(false) }, 1800)
}

async function syncNotificationSnapshot(force) {
  const prefs = notificationPreferences()
  if (!prefs.enabled) return { ok:false, disabled:true }
  if (isElectron) {
    evaluateDesktopNotifications(notificationSnapshot(), !!force)
    return { ok:true, mode:'desktop' }
  }
  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) return { ok:false, missingSubscription:true }
  return notificationRequest('/api/notifications/state', { endpoint:subscription.endpoint, deviceId:syncDeviceId(), preferences:prefs, snapshot:notificationSnapshot() })
}

const desktopAlertKeys = new Set()
function evaluateDesktopNotifications(snapshot, force) {
  const prefs = snapshot.preferences
  const today = localDateKey()
  const alerts = []
  if (prefs.penSupply) snapshot.loads.filter(function(load){return load.stage === 'in_use' && load.daysUntilRunout != null && load.daysUntilRunout >= 0 && load.daysUntilRunout <= prefs.daysBefore}).forEach(function(load){alerts.push({ key:'pen:'+load.key+':'+load.runoutDate, title:'Pen supply running low', body:load.name+' has about '+load.daysUntilRunout+' day(s) remaining.' })})
  if (prefs.vialPreparation) snapshot.loads.filter(function(load){return load.stage === 'upcoming' && load.protocolStart && daysBetween(today,load.protocolStart) >= 0 && daysBetween(today,load.protocolStart) <= prefs.daysBefore}).forEach(function(load){alerts.push({ key:'vial:'+load.key+':'+load.protocolStart, title:'Prepare upcoming vial load', body:load.name+' is needed for '+load.protocolName+'.' })})
  if (prefs.protocolStart) snapshot.protocols.filter(function(protocol){return protocol.status === 'upcoming' && protocol.startDate && daysBetween(today,protocol.startDate) >= 0 && daysBetween(today,protocol.startDate) <= prefs.daysBefore}).forEach(function(protocol){alerts.push({ key:'protocol:'+protocol.id+':'+protocol.startDate, title:'Protocol starting soon', body:protocol.name+' starts in '+daysBetween(today,protocol.startDate)+' day(s).' })})
  if (prefs.syncFailure && snapshot.syncError) alerts.push({ key:'sync:'+snapshot.syncError.at, title:'PeptideVault sync failed', body:snapshot.syncError.message })
  if (prefs.freezerTemperature && snapshot.freezer && Number(snapshot.freezer.temperature) > Number(prefs.freezerMaximumC)) alerts.push({ key:'freezer:'+today+':'+prefs.freezerMaximumC, title:'Freezer temperature warning', body:'Peptide freezer is '+Number(snapshot.freezer.temperature).toFixed(1)+'°C, above the '+prefs.freezerMaximumC+'°C limit.' })
  alerts.forEach(function(alert){ if (!desktopAlertKeys.has(alert.key) || force) { desktopAlertKeys.add(alert.key); window.api.showNotification({ title:alert.title, body:alert.body, tab:'dashboard' }) } })
}

async function testNotification() {
  try {
    if (isElectron) {
      await window.api.showNotification({ title:'PeptideVault test notification', body:'Notifications are working on this computer.', tab:'dashboard' })
    } else {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      await notificationRequest('/api/notifications/test', { endpoint:subscription && subscription.endpoint || '' })
    }
    showToast('Test notification sent')
  } catch(error) { showToast(error.message || 'Notification test failed','error') }
}

async function enableNotificationsUI(){ try { await enableNotifications(); showToast('Notifications enabled') } catch(error){ showToast(error.message,'error') } }
async function disableNotificationsUI(){ await disableNotifications(); showToast('Notifications disabled') }

export { NOTIFICATION_DEFAULTS, notificationPreferences, notificationSnapshot, enableNotifications, disableNotifications, saveNotificationPreferences, syncNotificationSnapshotSoon, syncNotificationSnapshot, testNotification, enableNotificationsUI, disableNotificationsUI }
